"use client";

import { useEffect, useState } from "react";
import type { EmailDeliveryStatus } from "@/lib/email/types";
export interface OtpDeliveryAttempt {
  receipt: string | null;
  sentAt: number;
  email: string;
}

// HTTP-date shapes (RFC 9110 §5.6.7), including the two legacy formats.
// Date.parse alone also accepts malformed delay values such as "9999.5".
const HTTP_DATE_FORMATS = [/^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} GMT$/, /^[A-Z][a-z]+, \d{2}-[A-Z][a-z]{2}-\d{2} \d{2}:\d{2}:\d{2} GMT$/, /^[A-Z][a-z]{2} [A-Z][a-z]{2} [ \d]\d \d{2}:\d{2}:\d{2} \d{4}$/];
function retryDelay(retryAfter: string | null): number {
  const value = retryAfter?.trim();
  if (!value) return 60_000;
  let delay: number;
  if (/^\d+$/.test(value)) {
    delay = Number(value) * 1_000;
  } else if (HTTP_DATE_FORMATS.some(format => format.test(value))) {
    // The legacy asctime format omits its zone, but HTTP dates are always UTC.
    delay = Date.parse(value.endsWith("GMT") ? value : `${value} GMT`) - Date.now();
  } else {
    return 60_000;
  }
  // An elapsed date needs no additional backoff beyond the normal poll interval.
  return Number.isFinite(delay) ? Math.max(5_000, delay) : 60_000;
}

/** Poll only this send attempt; abort and discard stale responses after edits/resends. */
export function useOtpDeliveryStatus(attempt: OtpDeliveryAttempt | null) {
  const [result, setResult] = useState<{
    attempt: OtpDeliveryAttempt;
    status: EmailDeliveryStatus;
  } | null>(null);
  const [helpAttempt, setHelpAttempt] = useState<OtpDeliveryAttempt | null>(null);
  useEffect(() => {
    if (!attempt) return;
    const timer = setTimeout(() => setHelpAttempt(attempt), Math.max(0, attempt.sentAt + 30_000 - Date.now()));
    return () => clearTimeout(timer);
  }, [attempt]);
  useEffect(() => {
    if (!attempt?.receipt) return;
    const controller = new AbortController();
    const deadline = attempt.sentAt + 120_000;
    let status: EmailDeliveryStatus = "unknown";
    let timer_0: ReturnType<typeof setTimeout>;
    const stopTimer = setTimeout(() => controller.abort(), Math.max(0, deadline - Date.now()));
    const poll = async () => {
      if (controller.signal.aborted || Date.now() >= deadline) return;
      let delay = 5_000;
      try {
        const response = await fetch("/api/auth/email/delivery-status", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            receipt: attempt.receipt
          }),
          cache: "no-store",
          signal: controller.signal
        });
        if (response.status === 429) {
          delay = retryDelay(response.headers.get("Retry-After"));
        } else if (response.ok) {
          const json = await response.json();
          if (["requested", "accepted", "delayed", "failed"].includes(json?.data?.status)) {
            status = json.data.status;
          }
        }
      } catch {
        /* A lookup failure must not erase this attempt's last known delivery status. */
      }
      if (controller.signal.aborted) return;
      setResult({
        attempt,
        status
      });
      // SMTP acceptance can still be followed by a bounce. Keep checking while
      // the unverified visitor waits, until failure or the two-minute deadline.
      if (status !== "failed" && Date.now() + delay < deadline) {
        timer_0 = setTimeout(poll, delay);
      }
    };
    timer_0 = setTimeout(poll, 5_000);
    return () => {
      controller.abort();
      clearTimeout(timer_0);
      clearTimeout(stopTimer);
    };
  }, [attempt]);
  const status_0 = result?.attempt === attempt ? result.status : attempt?.receipt ? "requested" : "unknown";
  return {
    status: status_0,
    showRecovery: !!attempt && (helpAttempt === attempt || status_0 === "failed" || status_0 === "delayed")
  };
}